/**
 * Outil « Suites » (Boîte à outils, 1re → prépa).
 *
 * Arithmétique, géométrique, arithmético-géométrique (curseurs, formules
 * exactes) et, à partir de la Tle, uₙ₊₁ = f(uₙ) avec f tapée par l'élève.
 * Deux vues : le nuage (n ; uₙ) et la toile d'araignée entre y = f(x) et
 * y = x. Sous la figure : formule explicite, sens de variation, limite
 * justifiée, bande ε (rang à partir duquel on y reste), seuil avec son
 * programme Python, somme des termes. Défi : retrouver une suite d'après
 * son nuage. Calculs dans `sequences.ts`.
 */

import { useMemo, useRef, useState } from 'react';
import { MathText, cn, sampleFunctionPath } from '@jeraisonne/ui';
import { schoolLevelRank } from '@jeraisonne/db/cards';
import { SendToSheetButton } from '../../send-to-sheet-button';
import type { ToolProps } from '../../tool-types';
import {
  Note,
  NumberField,
  ParamSlider,
  PRIMARY_BUTTON,
  SECONDARY_BUTTON,
  Segmented,
  ToggleChip,
} from '../../ui/controls';
import type { ExactNumber } from '../../ui/exact';
import { clamp, makeFrame, type Frame } from '../../ui/frame';
import { formatNumber, formatSci } from '../../ui/format';
import { FIGURE_BG_CLASS, HaloText, INK, PlotGrid } from '../../ui/svg-parts';
import { useDrag } from '../../ui/use-drag';
import { compileFormula } from '../function-plotter/formula';
import {
  analyseRecur,
  arithmeticThreshold,
  auxiliary,
  CHALLENGE_ROUNDS,
  cobweb,
  computeTerms,
  derivativeAt,
  exactTerm,
  exactBandRank,
  explicitFormula,
  firstRank,
  fitRange,
  fixedPoints,
  limitOf,
  limitReason,
  matchesTarget,
  monotony,
  pickTarget,
  RANGES,
  rankInBand,
  RECUR_PRESETS,
  recurStep,
  recurrenceTex,
  stepOf,
  sumOf,
  thresholdProgram,
  type ExactKind,
  type Monotony,
  type SeqKind,
  type SeqParams,
  type SeqTarget,
  type StepFn,
} from './sequences';

const SIZE = 320;
const POINT = 'var(--color-primary-500)';
const LIMIT = 'var(--color-xp-500)';
const WEB = 'var(--color-accent-500)';
const IN_BAND = 'var(--color-success-500)';
const TARGET = 'var(--color-ink-500)';
/** au-delà, un terme sort du cadre (sinon les premiers seraient écrasés) */
const CAP = 1e6;
const HORIZON = 3000;
const EPS_OPTIONS = ['1', '0.1', '0.01', '0.001'] as const;

const KIND_LABELS: Record<SeqKind, string> = {
  arith: 'Arithmétique',
  geom: 'Géométrique',
  arigeo: 'Arithmético-géo.',
  recur: 'uₙ₊₁ = f(uₙ)',
};

const MONOTONY_TEXT: Record<Monotony, string> = {
  increasing: 'croissante',
  decreasing: 'décroissante',
  constant: 'constante',
  constantFrom1: 'constante à partir du rang 1',
  none: 'ni croissante ni décroissante (les termes alternent)',
};

const SUB = '₀₁₂₃₄₅₆₇₈₉';
const sub = (k: number) =>
  String(k)
    .split('')
    .map((c) => SUB[Number(c)] ?? c)
    .join('');
const fmt = (v: number) => formatNumber(v, 3);
/** 5,1 × 10⁻⁶ pour les très petits écarts (fmt les arrondirait à 0) */
const small = (v: number) => (v === 0 || v >= 0.001 ? fmt(v) : formatSci(v, 1));

interface Challenge {
  round: number;
  target: SeqTarget;
}

interface LimitInfo {
  value: number | null;
  exact: ExactNumber | null;
  /** 1 : +∞, −1 : −∞, 0 : sinon */
  diverge: 1 | -1 | 0;
  headline: string;
  reason: string;
  tone: 'success' | 'error' | 'neutral';
  /** limite démontrée (familles exactes) ou seulement observée */
  proven: boolean;
  constant: boolean;
}

export function SequencesTool({
  onSend,
  sendCost,
  level,
  rng = Math.random,
}: ToolProps & { level?: string | null; rng?: () => number }) {
  const rank = schoolLevelRank(level);
  const kinds: SeqKind[] =
    rank === null || rank >= 7 ? ['arith', 'geom', 'arigeo', 'recur'] : ['arith', 'geom', 'arigeo'];
  const [kind, setKind] = useState<SeqKind>('arith');
  const [p, setP] = useState<SeqParams>({ u0: 3, r: 2, q: 0.5, a: 0.5, b: 3 });
  const [formula, setFormula] = useState(RECUR_PRESETS[0]!.formula);
  const [u0Recur, setU0Recur] = useState(RECUR_PRESETS[0]!.u0);
  const [count, setCount] = useState(15);
  const [view, setView] = useState<'cloud' | 'cobweb'>('cloud');
  const [band, setBand] = useState(false);
  const [eps, setEps] = useState<(typeof EPS_OPTIONS)[number]>('0.1');
  const [threshold, setThreshold] = useState(1000);
  const [picked, setPicked] = useState<number | null>(null);
  const [challenge, setChallenge] = useState<Challenge | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  // uₙ₊₁ = f(uₙ) : la dernière formule valide reste tracée pendant la frappe
  const compiled = useMemo(() => compileFormula(formula), [formula]);
  const formulaError = !compiled.ok
    ? compiled.error
    : compiled.params.length > 0
      ? 'Ici, f ne dépend que de x : pas de lettre a, b, c…'
      : null;
  const lastExpr = useRef<string | null>(null);
  if (compiled.ok && compiled.params.length === 0) lastExpr.current = compiled.expr;
  const recurExpr = lastExpr.current;

  const exactKind: ExactKind | null = kind === 'recur' ? null : kind;
  const step: StepFn = useMemo(() => {
    if (exactKind) return stepOf(exactKind, p);
    const e = recurExpr;
    return e ? recurStep(e) : () => NaN;
  }, [exactKind, p, recurExpr]);
  const u0 = exactKind ? p.u0 : u0Recur;
  const terms = useMemo(() => computeTerms(step, u0, count), [step, u0, count]);
  const longTerms = useMemo(() => computeTerms(step, u0, HORIZON), [step, u0]);

  const info: LimitInfo = useMemo(() => {
    if (exactKind) {
      const L = limitOf(exactKind, p);
      const reason = limitReason(exactKind, p);
      if (L.type === 'finite')
        return {
          value: L.value.value,
          exact: L.value,
          diverge: 0,
          headline: L.constant
            ? `Suite constante${monotony(exactKind, p) === 'constantFrom1' ? ' à partir du rang 1' : ''} : limite ${L.value.plain}.`
            : `La suite converge vers ${L.value.plain}.`,
          reason,
          tone: 'success',
          proven: true,
          constant: L.constant,
        };
      if (L.type === 'plusInf' || L.type === 'minusInf')
        return {
          value: null,
          exact: null,
          diverge: L.type === 'plusInf' ? 1 : -1,
          headline: `La suite tend vers ${L.type === 'plusInf' ? '+∞' : '−∞'}.`,
          reason,
          tone: 'neutral',
          proven: true,
          constant: false,
        };
      return {
        value: null,
        exact: null,
        diverge: 0,
        headline: 'La suite n’a pas de limite.',
        reason,
        tone: 'error',
        proven: true,
        constant: false,
      };
    }
    const v = analyseRecur(step, u0, HORIZON);
    if (v.type === 'converges') {
      const d = v.derivative;
      const repulsive = d !== null && Math.abs(d) > 1.001;
      const reason = v.constant
        ? `f(u₀) = u₀ : u₀ est un point fixe de f, la suite est constante.${
            repulsive
              ? ` Ce point fixe est répulsif (f′ ≈ ${fmt(d!)}) : en partant juste à côté, la suite s’en éloignerait.`
              : ''
          }`
        : d === null
          ? 'ℓ est un point fixe : f(ℓ) = ℓ.'
          : Math.abs(d) < 0.999
            ? `ℓ est un point fixe de f, et f′(ℓ) ≈ ${fmt(d)} : |f′(ℓ)| < 1, le point fixe attire la suite.`
            : !repulsive
              ? `ℓ est un point fixe de f, et f′(ℓ) ≈ ${fmt(d)} : |f′(ℓ)| ≈ 1, la convergence est lente et ce critère ne permet pas de conclure.`
              : `ℓ est un point fixe répulsif (f′(ℓ) ≈ ${fmt(d)}) : la suite tombe pile dessus${
                  v.reachedAt !== null ? ` au rang ${v.reachedAt}` : ''
                }, mais en partant à côté elle s’en éloignerait.`;
      return {
        value: v.value,
        exact: null,
        diverge: 0,
        headline: v.constant
          ? `Suite constante égale à ≈ ${fmt(v.value)}.`
          : `La suite semble converger vers ℓ ≈ ${fmt(v.value)}.`,
        reason,
        tone: 'success',
        proven: false,
        constant: v.constant,
      };
    }
    if (v.type === 'alternateGrow')
      return {
        value: null,
        exact: null,
        diverge: 0,
        headline: 'La suite semble n’avoir aucune limite.',
        reason: '|uₙ| grandit sans fin mais les termes changent de signe : ni +∞ ni −∞.',
        tone: 'error',
        proven: false,
        constant: false,
      };
    if (v.type === 'plusInf' || v.type === 'minusInf')
      return {
        value: null,
        exact: null,
        diverge: v.type === 'plusInf' ? 1 : -1,
        headline: `La suite semble tendre vers ${v.type === 'plusInf' ? '+∞' : '−∞'}.`,
        reason: 'Les termes dépassent 10¹² en valeur absolue.',
        tone: 'neutral',
        proven: false,
        constant: false,
      };
    if (v.type === 'cycle2')
      return {
        value: null,
        exact: null,
        diverge: 0,
        headline: `La suite semble osciller entre ≈ ${fmt(v.values[0])} et ≈ ${fmt(v.values[1])}.`,
        reason: 'Cycle de période 2 : pas de limite.',
        tone: 'error',
        proven: false,
        constant: false,
      };
    if (v.type === 'undefined')
      return {
        value: null,
        exact: null,
        diverge: 0,
        headline: `u${sub(v.at)} n’existe pas.`,
        reason: `f n’est pas définie en u${sub(v.at - 1)} : la suite s’arrête.`,
        tone: 'error',
        proven: false,
        constant: false,
      };
    const moving = `Au rang ${HORIZON}, les termes bougent encore d’environ ${small(v.lastStep)} d’un rang à l’autre`;
    return {
      value: null,
      exact: null,
      diverge: 0,
      headline:
        v.trend === 'up'
          ? 'La suite semble croissante, sans limite lisible ici.'
          : v.trend === 'down'
            ? 'La suite semble décroissante, sans limite lisible ici.'
            : `Pas de limite visible au rang ${HORIZON}.`,
      reason:
        v.trend === 'mixed'
          ? `${moving} : aucune conjecture possible.`
          : `${moving}. Elle peut converger lentement ou tendre vers ${
              v.trend === 'up' ? '+∞' : '−∞'
            } : il faut une preuve.`,
      tone: 'neutral',
      proven: false,
      constant: false,
    };
  }, [exactKind, p, step, u0]);

  const epsValue = Number(eps);
  const bandRank =
    band && info.value !== null
      ? exactKind
        ? exactBandRank(exactKind, p, epsValue)
        : rankInBand(longTerms, info.value, epsValue)
      : null;
  const fixed = useMemo(() => {
    if (kind !== 'recur' || !recurExpr) return [];
    const fp = fixedPoints(step);
    return fp === 'all' ? 'all' : fp.map((x) => ({ x, d: derivativeAt(step, x) }));
  }, [kind, recurExpr, step]);

  const target = challenge?.target ?? null;
  const targetTerms = useMemo(
    () =>
      target
        ? computeTerms(
            stepOf(target.kind, { u0: target.u0, r: target.ratio, q: target.ratio, a: 0, b: 0 }),
            target.u0,
            count,
          )
        : [],
    [target, count],
  );
  const found = challenge !== null && matchesTarget(kind, p, challenge.target);

  const setParam = (k: keyof SeqParams) => (v: number) => {
    setP((old) => ({ ...old, [k]: v }));
    setPicked(null);
  };

  const startChallenge = () => {
    // jamais une cible déjà atteinte par les réglages actuels
    const k: SeqKind = kind === 'recur' ? 'arith' : kind;
    let t = pickTarget(rng);
    for (let i = 0; i < 20 && matchesTarget(k, p, t); i++) t = pickTarget(rng, t);
    setChallenge({ round: 1, target: t });
    setView('cloud');
    if (kind === 'recur') setKind('arith');
  };
  const nextRound = () =>
    challenge &&
    setChallenge({ round: challenge.round + 1, target: pickTarget(rng, challenge.target) });

  // ---------------------------------------------------------------- figure
  const shown = useMemo(
    () => terms.map((v) => (Number.isFinite(v) && Math.abs(v) <= CAP ? v : NaN)),
    [terms],
  );
  // premier terme trop grand pour le cadre (un terme qui n'existe pas n'en est pas un)
  const offFrame = terms.findIndex((v) => !Number.isNaN(v) && !(Math.abs(v) <= CAP));
  const frame: Frame = useMemo(() => {
    if (view === 'cloud') {
      const values = [
        ...shown,
        ...targetTerms.filter((v) => Math.abs(v) <= CAP),
        ...(info.value !== null ? [info.value] : []),
        ...(band && info.value !== null ? [info.value - epsValue, info.value + epsValue] : []),
      ];
      const [lo, hi] = fitRange(values);
      return makeFrame({ xMin: -0.8, xMax: count + 0.8, yMin: lo, yMax: hi }, SIZE);
    }
    const values = [...shown, 0, ...(info.value !== null ? [info.value] : [])];
    const [lo, hi] = fitRange(values);
    return makeFrame({ xMin: lo, xMax: hi, yMin: lo, yMax: hi }, SIZE);
  }, [view, shown, targetTerms, info.value, band, epsValue, count]);

  const drag = useDrag<never>({
    svgRef,
    frame,
    ids: [],
    pointOf: () => ({ x: 0, y: 0 }),
    onDrag: () => {},
    onBackground: (pt) => {
      if (view !== 'cloud') return;
      setPicked(clamp(Math.round(pt.x), 0, count));
    },
  });

  const pickedValue = picked !== null ? terms[picked] : undefined;
  const pickedExact = picked !== null && exactKind ? exactTerm(exactKind, p, picked) : null;

  const thresholdDir = info.diverge;
  /** « u₄₉₉ = 1001 » exact quand c'est possible, sinon « u₁₅ ≈ 1313,682 » */
  const thresholdTermText = (n: number) => {
    const ex = exactKind ? exactTerm(exactKind, p, n) : null;
    if (ex && ex.plain.length <= 14) return `u${sub(n)} = ${ex.plain}`;
    return `u${sub(n)} ≈ ${fmt(longTerms[n] ?? NaN)}`;
  };
  const thresholdTarget = thresholdDir * threshold;
  const thresholdRank =
    thresholdDir === 0
      ? null
      : ((exactKind && arithmeticThreshold(exactKind, p, thresholdTarget, thresholdDir)) ??
        firstRank(step, u0, thresholdTarget, thresholdDir));

  const vp = frame.vp;
  const curvePath = useMemo(
    () => (view === 'cobweb' ? sampleFunctionPath(step, [vp.xMin, vp.xMax], frame) : ''),
    [view, step, vp.xMin, vp.xMax, frame],
  );

  return (
    <div className="space-y-4">
      <Segmented
        ariaLabel="Type de suite"
        value={kind}
        options={kinds.map((k) => ({ value: k, label: KIND_LABELS[k] }))}
        onChange={(k) => {
          setKind(k);
          setPicked(null);
        }}
      />

      {challenge && (
        <section
          aria-live="polite"
          className="bg-xp-50 space-y-2 rounded-2xl p-3"
          data-testid="seq-challenge"
        >
          <p className="text-ink-900 text-sm font-semibold">
            Suite {challenge.round}/{CHALLENGE_ROUNDS} · Retrouve la suite des ronds vides : son
            type, u₀ et sa raison.
          </p>
          {found ? (
            <div className="flex flex-wrap items-center gap-2">
              <p className="text-success-600 text-sm font-bold">
                Trouvé : <MathText>{`$${explicitFormula(challenge.target.kind, p).tex}$`}</MathText>
              </p>
              {challenge.round < CHALLENGE_ROUNDS ? (
                <button type="button" onClick={nextRound} className={cn(PRIMARY_BUTTON, 'ml-auto')}>
                  Suite suivante
                </button>
              ) : (
                <>
                  <button type="button" onClick={startChallenge} className={PRIMARY_BUTTON}>
                    Rejouer
                  </button>
                  <button
                    type="button"
                    onClick={() => setChallenge(null)}
                    className={SECONDARY_BUTTON}
                  >
                    Fin du défi
                  </button>
                </>
              )}
            </div>
          ) : (
            <p className="text-ink-600 text-sm">
              Astuce : u₀ se lit en n = 0. Si les écarts entre termes sont égaux, c’est une suite
              arithmétique ; si ce sont les quotients, géométrique.
            </p>
          )}
        </section>
      )}

      {kind === 'recur' ? (
        <div className="space-y-3">
          <div className="flex flex-wrap gap-2" role="group" aria-label="Exemples de f">
            {RECUR_PRESETS.map((pr) => (
              <button
                key={pr.label}
                type="button"
                aria-pressed={formula === pr.formula && u0Recur === pr.u0}
                onClick={() => {
                  setFormula(pr.formula);
                  setU0Recur(pr.u0);
                  setPicked(null);
                }}
                className={cn(
                  'min-h-11 rounded-xl border px-3 text-sm font-semibold transition-colors active:scale-[0.98]',
                  formula === pr.formula && u0Recur === pr.u0
                    ? 'border-primary-500 bg-primary-50 text-primary-700'
                    : 'border-ink-200 text-ink-600 hover:bg-ink-50',
                )}
              >
                {pr.label}
              </button>
            ))}
          </div>
          <label className="block">
            <span className="text-ink-600 mb-1 block text-sm font-semibold">f(x) =</span>
            <input
              type="text"
              value={formula}
              onChange={(e) => setFormula(e.target.value)}
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck={false}
              aria-invalid={formulaError !== null}
              aria-describedby="seq-formula-help"
              className={cn(
                'text-ink-900 ocean:bg-ink-100 min-h-12 w-full rounded-xl border bg-white px-3 font-mono text-base',
                formulaError ? 'border-error-500' : 'border-ink-200',
              )}
            />
          </label>
          <p
            id="seq-formula-help"
            className={cn('text-sm', formulaError ? 'text-error-600' : 'text-ink-500')}
            aria-live="polite"
          >
            {formulaError ?? 'Écris f comme sur ta copie : √(x + 2), x² + 0,1, cos(x)…'}
          </p>
          <ParamSlider
            name="u₀"
            ariaLabel="Valeur de u₀"
            value={u0Recur}
            min={-5}
            max={5}
            step={0.1}
            format={(v) => formatNumber(v, 1)}
            onChange={(v) => {
              setU0Recur(v);
              setPicked(null);
            }}
          />
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <ParamSlider
            name="u₀"
            ariaLabel="Valeur de u₀"
            value={p.u0}
            {...RANGES.u0}
            format={(v) => formatNumber(v, 1)}
            onChange={setParam('u0')}
          />
          {kind === 'arith' && (
            <ParamSlider
              name="raison r"
              ariaLabel="Raison r"
              value={p.r}
              {...RANGES.r}
              format={(v) => formatNumber(v, 1)}
              onChange={setParam('r')}
            />
          )}
          {kind === 'geom' && (
            <ParamSlider
              name="raison q"
              ariaLabel="Raison q"
              value={p.q}
              {...RANGES.q}
              format={(v) => formatNumber(v, 1)}
              onChange={setParam('q')}
            />
          )}
          {kind === 'arigeo' && (
            <>
              <ParamSlider
                name="a"
                ariaLabel="Coefficient a"
                value={p.a}
                {...RANGES.a}
                format={(v) => formatNumber(v, 1)}
                onChange={setParam('a')}
              />
              <ParamSlider
                name="b"
                ariaLabel="Constante b"
                value={p.b}
                {...RANGES.b}
                format={(v) => formatNumber(v, 1)}
                onChange={setParam('b')}
              />
            </>
          )}
        </div>
      )}

      {exactKind && <FormulaCard kind={exactKind} p={p} />}

      <div className="flex flex-wrap items-center gap-2">
        <Segmented
          ariaLabel="Vue"
          value={view}
          options={[
            { value: 'cloud', label: 'Nuage' },
            { value: 'cobweb', label: 'Toile d’araignée' },
          ]}
          onChange={(v) => {
            setView(v);
            setPicked(null);
          }}
        />
        <ToggleChip
          on={challenge !== null}
          onClick={() => (challenge ? setChallenge(null) : startChallenge())}
        >
          Défi
        </ToggleChip>
      </div>

      <div className="mx-auto w-full max-w-[26rem]">
        <svg
          ref={svgRef}
          viewBox={`0 0 ${SIZE} ${SIZE}`}
          className={cn(
            'border-ink-100 block aspect-square w-full touch-none select-none rounded-2xl border',
            FIGURE_BG_CLASS,
          )}
          role="img"
          aria-label={
            view === 'cloud'
              ? `Nuage des points (n ; uₙ) pour n de 0 à ${count}. Touche un point pour lire uₙ.`
              : 'Toile d’araignée : la courbe de f, la droite y = x et l’escalier des termes.'
          }
          data-testid="seq-figure"
          {...drag.svgProps}
        >
          <PlotGrid frame={frame} />
          {view === 'cloud' ? (
            <CloudLayer
              frame={frame}
              terms={shown}
              target={targetTerms}
              limit={info.value}
              band={band ? epsValue : null}
              bandRank={bandRank}
              picked={picked}
            />
          ) : (
            <CobwebLayer frame={frame} terms={shown} curvePath={curvePath} limit={info.value} />
          )}
        </svg>
      </div>

      <ParamSlider
        name="Termes affichés"
        ariaLabel="Nombre de termes affichés"
        value={count}
        min={5}
        max={40}
        step={1}
        format={(v) => `u₀ à u${sub(v)}`}
        onChange={(v) => {
          setCount(v);
          setPicked((old) => (old !== null && old > v ? null : old));
        }}
      />

      {offFrame > 0 && (
        <p className="text-ink-500 text-sm">
          À partir de u{sub(offFrame)}, les termes dépassent 10⁶ en valeur absolue : ils sortent du
          cadre.
        </p>
      )}

      {view === 'cloud' && (
        <div className="flex min-h-12 flex-wrap items-center gap-2">
          <p
            className="text-ink-900 min-w-0 flex-1 text-base font-semibold"
            aria-live="polite"
            data-testid="seq-reading"
          >
            {picked === null || pickedValue === undefined
              ? 'Touche un point du nuage pour lire uₙ.'
              : !Number.isFinite(pickedValue)
                ? `u${sub(picked)} n’existe pas.`
                : pickedExact && pickedExact.plain.length <= 14
                  ? `u${sub(picked)} = ${pickedExact.plain}`
                  : `u${sub(picked)} ≈ ${fmt(pickedValue)}`}
          </p>
          {onSend && pickedExact && pickedExact.plain.length <= 14 && picked !== null && (
            <SendToSheetButton
              label={pickedExact.plain}
              cost={sendCost}
              onClick={() => onSend({ atoms: pickedExact.atoms })}
            />
          )}
        </div>
      )}

      <section
        aria-label="Limite"
        className={cn(
          'space-y-1 rounded-2xl px-3 py-2',
          info.tone === 'success' && 'bg-success-50',
          info.tone === 'error' && 'bg-error-50',
          info.tone === 'neutral' && 'bg-ink-50',
        )}
        data-testid="seq-limit"
      >
        <div className="flex min-h-11 items-center gap-2">
          <p className="text-ink-900 min-w-0 flex-1 text-base font-bold">{info.headline}</p>
          {onSend && info.exact && (
            <SendToSheetButton
              label={info.exact.plain}
              cost={sendCost}
              onClick={() => onSend({ atoms: info.exact!.atoms })}
            />
          )}
        </div>
        <p className="text-ink-700 text-sm">{info.reason}</p>
        {!info.proven && (
          <p className="text-ink-500 text-xs">
            Étude numérique sur {HORIZON} termes : c’est une conjecture, pas une preuve.
          </p>
        )}
      </section>

      {kind === 'recur' && fixed === 'all' && (
        <Note live={false}>f(x) = x pour tout x : chaque réel est un point fixe.</Note>
      )}
      {kind === 'recur' && fixed !== 'all' && fixed.length > 0 && (
        <Note live={false}>
          Points fixes de f (f(ℓ) = ℓ) entre −10 et 10 :{' '}
          {fixed
            .slice(0, 6)
            .map(
              ({ x, d }) =>
                `ℓ ≈ ${fmt(x)}${
                  d === null
                    ? ''
                    : ` (f′(ℓ) ≈ ${fmt(d)}, ${
                        Math.abs(d) < 0.999
                          ? 'attractif'
                          : Math.abs(d) > 1.001
                            ? 'répulsif'
                            : 'on ne peut pas conclure'
                      })`
                }`,
            )
            .join(' ; ')}
          {fixed.length > 6 ? ' ; …' : ''}.
        </Note>
      )}

      {info.value !== null && !info.constant && (
        <section className="border-ink-100 space-y-2 rounded-2xl border px-3 py-2">
          <div className="flex flex-wrap items-center gap-2">
            <ToggleChip on={band} onClick={() => setBand((v) => !v)}>
              Bande ε
            </ToggleChip>
            {band && (
              <Segmented
                ariaLabel="Largeur ε"
                value={eps}
                options={EPS_OPTIONS.map((e) => ({
                  value: e,
                  label: `ε = ${e.replace('.', ',')}`,
                }))}
                onChange={setEps}
              />
            )}
          </div>
          {band && (
            <p className="text-ink-800 text-sm" aria-live="polite" data-testid="seq-band">
              {bandRank === null
                ? `Au rang ${HORIZON}, la suite n’est pas encore restée dans la bande.`
                : `|uₙ − ℓ| < ${eps.replace('.', ',')} pour tout n ≥ ${bandRank}${
                    info.proven
                      ? ' : à partir de là, l’écart ne fait que diminuer.'
                      : ` (vérifié jusqu’au rang ${HORIZON}).`
                  }`}
            </p>
          )}
          {!band && (
            <p className="text-ink-600 text-sm">
              Resserre une bande autour de la limite : à partir de quel rang la suite y reste-t-elle
              ?
            </p>
          )}
        </section>
      )}

      {thresholdDir !== 0 && (
        <section className="border-ink-100 space-y-2 rounded-2xl border px-3 py-2">
          <p className="text-ink-700 text-sm font-semibold">
            Seuil : premier rang n tel que uₙ {thresholdDir === 1 ? '>' : '<'}{' '}
            {thresholdDir === 1 ? '' : '−'}A
          </p>
          <div className="max-w-[16rem]">
            <NumberField
              label="A"
              value={threshold}
              min={1}
              max={1000000}
              onChange={setThreshold}
            />
          </div>
          <div className="flex min-h-11 items-center gap-2">
            <p
              className="text-ink-900 min-w-0 flex-1 text-base font-bold"
              data-testid="seq-threshold"
            >
              {thresholdRank === null
                ? 'Pas trouvé avant le rang 100 000.'
                : `n = ${thresholdRank} (${thresholdTermText(thresholdRank)})`}
            </p>
            {onSend && thresholdRank !== null && (
              <SendToSheetButton
                label={String(thresholdRank)}
                cost={sendCost}
                onClick={() => onSend({ atoms: [String(thresholdRank)] })}
              />
            )}
          </div>
          <pre
            className="bg-ink-50 text-ink-800 overflow-x-auto rounded-xl p-3 font-mono text-sm"
            aria-label="Programme Python de l’algorithme de seuil"
          >
            {thresholdProgram(
              kind,
              { ...p, u0 },
              thresholdTarget,
              thresholdDir,
              recurExpr ?? undefined,
            )}
          </pre>
        </section>
      )}

      {(kind === 'arith' || kind === 'geom') && <SumCard kind={kind} p={p} n={count} />}
    </div>
  );
}

function FormulaCard({ kind, p }: { kind: ExactKind; p: SeqParams }) {
  const ex = explicitFormula(kind, p);
  const aux = kind === 'arigeo' ? auxiliary(p) : null;
  return (
    <section
      className="border-ink-100 space-y-1 rounded-2xl border px-3 py-2"
      aria-label="Formules"
      data-testid="seq-formula"
    >
      <MathText className="text-ink-900 block overflow-x-auto text-base">
        {`$${recurrenceTex(kind, p)}$`}
      </MathText>
      <MathText className="text-ink-900 block overflow-x-auto text-base">
        {`$${ex.tex}${ex.from > 0 ? '\\quad (n \\geq 1)' : ''}$`}
      </MathText>
      {aux && (
        <p className="text-ink-700 text-sm">
          Suite auxiliaire (géométrique de raison {formatNumber(p.a, 1)}) :{' '}
          <MathText>{`$${aux.tex}$`}</MathText>
        </p>
      )}
      <p className="text-ink-600 text-sm">
        Sens de variation : {MONOTONY_TEXT[monotony(kind, p)]}.
      </p>
    </section>
  );
}

function SumCard({ kind, p, n }: { kind: 'arith' | 'geom'; p: SeqParams; n: number }) {
  const s = sumOf(kind, p, n);
  return (
    <section className="border-ink-100 space-y-1 rounded-2xl border px-3 py-2" aria-label="Somme">
      <MathText className="text-ink-900 block overflow-x-auto text-base">
        {`$\\displaystyle ${s.formula}$`}
      </MathText>
      <p className="text-ink-700 text-sm" data-testid="seq-sum">
        Ici : u₀ + … + u{sub(n)} {s.exact ? `= ${s.exact.plain}` : `≈ ${formatNumber(s.value, 3)}`}
      </p>
    </section>
  );
}

function CloudLayer({
  frame,
  terms,
  target,
  limit,
  band,
  bandRank,
  picked,
}: {
  frame: Frame;
  terms: readonly number[];
  target: readonly number[];
  limit: number | null;
  band: number | null;
  bandRank: number | null;
  picked: number | null;
}) {
  const { tx, ty, size } = frame;
  return (
    <g>
      {limit !== null && band !== null && (
        <rect
          x={0}
          y={ty(limit + band)}
          width={size}
          height={Math.max(1, ty(limit - band) - ty(limit + band))}
          fill={LIMIT}
          fillOpacity={0.16}
          data-testid="seq-band-rect"
        />
      )}
      {limit !== null && (
        <line
          x1={0}
          y1={ty(limit)}
          x2={size}
          y2={ty(limit)}
          stroke={LIMIT}
          strokeWidth={2}
          strokeDasharray="7 5"
        />
      )}
      {target.map((v, n) =>
        Number.isFinite(v) ? (
          <circle
            key={`t${n}`}
            cx={tx(n)}
            cy={ty(v)}
            r={7}
            fill="none"
            stroke={TARGET}
            strokeWidth={2}
          />
        ) : null,
      )}
      {terms.map((v, n) =>
        Number.isFinite(v) ? (
          <circle
            key={n}
            cx={tx(n)}
            cy={ty(v)}
            r={picked === n ? 6 : 4.5}
            fill={bandRank !== null && n >= bandRank ? IN_BAND : POINT}
            stroke={picked === n ? WEB : 'none'}
            strokeWidth={3}
            data-testid="seq-point"
          />
        ) : null,
      )}
      {limit !== null && (
        <HaloText
          x={size - 6}
          y={ty(limit) - 6}
          textAnchor="end"
          fontSize={13}
          fontWeight={700}
          fill={LIMIT}
        >
          ℓ
        </HaloText>
      )}
    </g>
  );
}

function CobwebLayer({
  frame,
  terms,
  curvePath,
  limit,
}: {
  frame: Frame;
  terms: readonly number[];
  curvePath: string;
  limit: number | null;
}) {
  const { tx, ty, vp } = frame;
  const web = cobweb(terms);
  const labels: Array<{ n: number; x: number }> = [];
  for (let n = 0; n < Math.min(terms.length, 4); n++) {
    const v = terms[n]!;
    if (!Number.isFinite(v)) break;
    if (labels.every((l) => Math.abs(tx(l.x) - tx(v)) > 22)) labels.push({ n, x: v });
  }
  return (
    <g>
      <line
        x1={tx(vp.xMin)}
        y1={ty(vp.xMin)}
        x2={tx(vp.xMax)}
        y2={ty(vp.xMax)}
        stroke="var(--color-ink-400)"
        strokeWidth={1.5}
      />
      <path d={curvePath} fill="none" stroke={POINT} strokeWidth={2.75} />
      {web.length > 1 && (
        <polyline
          points={web.map(([x, y]) => `${tx(x)},${ty(y)}`).join(' ')}
          fill="none"
          stroke={WEB}
          strokeWidth={1.75}
          strokeLinejoin="round"
          data-testid="seq-cobweb"
        />
      )}
      {limit !== null && (
        <circle cx={tx(limit)} cy={ty(limit)} r={5} fill={LIMIT} stroke={INK} strokeWidth={1} />
      )}
      {labels.map(({ n, x }) => (
        <HaloText
          key={n}
          x={tx(x)}
          y={Math.max(14, ty(0) - 8)}
          textAnchor="middle"
          fontSize={13}
          fontWeight={700}
          fill={WEB}
        >
          {`u${sub(n)}`}
        </HaloText>
      ))}
    </g>
  );
}
